import React, { useEffect, useState } from "react";
import "./style.scss";
import defaultImg from "../../assets/images/default.webp";
import {AdditionalSellUnit, MenuItem} from "../../@types/stores/menu";
import { useTranslation } from "react-i18next";
import lodash from "lodash";
import Counter from "../Counter";
import orderStore from "../../stores/order";
import ModifiersGroup from "./ModifiersGroup";
import { OrderLineAddon, OrderLineAddons, OrderLineIngredient, OrderLineIngredients, OrderLineModifiers } from "../../@types/stores/order";
import AddonsGroup from "./AddonsGroup";
import { v4 as guid } from 'uuid';
import { hideLoader, showInfo, showLoader } from "../../helpers/ComponentHelper";
import { useNavigate } from "react-router";
import {getAllergenById, getCurrency, getHost, uniqArray} from "../../helpers/ApiHelper";
import IngredientsGroup from "./IngredientsGroup";
import { getIngredientsForOrderLine } from "../../helpers/orderHelper";
import useLayoutStyles from "../../hooks/styles";
import menuStore from "../../stores/menu";
import SellUnitsGroup from "./SellUnitsGroup";

export interface MenuItemProps {
    menuId?: number;
    subMenuId?: number;
    recommendationGroupId?: number;
    item: MenuItem;
    skipRecommendations?: boolean
}

const MenuItemBlock = ({item, skipRecommendations, menuId, subMenuId, recommendationGroupId}: MenuItemProps) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const styles = useLayoutStyles();

    const [count, setCount] = useState<number>(1);
    const [price, setPrice] = useState<number>(item.price);
    const [modifiers, setModifiers] = useState<OrderLineModifiers[]>([]);
    const [addons, setAddons] = useState<OrderLineAddons[]>([]);
    const [allowAddToOrder, setAllowAddToOrer] = useState<boolean>(true);
    const [free, setFree] = useState<number>(item.freeAddons || 0);
    const [ingredients, setIngredients] = useState<OrderLineIngredients[]>([]);
    const [additionalSellUnit, setAdditionalSellUnit] = useState<AdditionalSellUnit | undefined>(item.additionalSellUnits?.find(x => x.isDefault));

    useEffect(() => {
        if (item.ingredientGroups) {
            setDefaultSelectedItems();
        }
    }, []);

    useEffect(() => {
        checkAllowAddToOrder();
    }, [item, modifiers, ingredients]);

    useEffect(() => {
        reCalcPrice();
    }, [item, addons, ingredients, additionalSellUnit]);

    useEffect(() => {
        reCalcFreeAddons();
    }, [item, addons]);


    const setDefaultSelectedItems = () => {
        const defaultSelectedGropus = item.ingredientGroups?.filter(ig => ig.items.some(i => !!i.default));
        if (defaultSelectedGropus) {
            const _selected: OrderLineIngredients[] = [];
            defaultSelectedGropus.forEach(ig => {
                _selected.push({
                    groupId: ig.id,
                    items: ig.items.filter(i => !!i.default).map(i => {
                        return {
                            id: i.id,
                            number: i.number,
                            quantity: i.quantity,
                            price: i.unitPrice,
                            sellUnit: i.sellUnit,
                            default: i.default || false
                        };
                    })
                })
            });
            setIngredients(_selected);
        }
    }

    const reCalcFreeAddons = () => {
        const count = addons.map(x => x.items).flat().length;
        const _free = item.freeAddons || 0;
        setFree(_free - count > 0 ? _free - count : 0);
    }

    const checkAllowAddToOrder = () => {
        let allow = true;

        // Check modifiers
        if (item.modifierGroups) {
            for (const group of item.modifierGroups) {
                const selectedModifierCount = modifiers.find(x => x.groupId === group.id)?.items.length || 0;
                const min = group.min || 0;
                if (selectedModifierCount < min)
                    allow = false;
            }
        }

        // Check ingredients
        if (allow && item.ingredientGroups) {
            for (const group of item.ingredientGroups) {
                const selectedCount = ingredients.find(x => x.groupId === group.id)?.items.length || 0;
                const min = group.min || 0;
                if (selectedCount < min)
                    allow = false;
            }
        }

        setAllowAddToOrer(allow);
    }

    const reset = () => {
        setCount(1);
        setPrice(0);
        setModifiers([]);
        setAddons([]);
    }

    const reCalcPrice = () => {
        const _ingredients = getIngredientsForOrderLine(ingredients, item.ingredientGroups);

        const ol = orderStore.reCalcOrderLine({
            id: guid(),
            menuId,
            subMenuId,
            recommendationGroupId,
            name: item.name,
            number: item.number,
            modifiers: modifiers,
            addons: addons,
            ingredients: _ingredients,
            count,
            price: additionalSellUnit?.price || item.price,
            total: price,
            img: item.previewList,
            sellUnitId: additionalSellUnit?.sellUnitId,
            sellFactor: additionalSellUnit?.sellFactor
        }).orderLine;

        setPrice(ol.total);
    }

    const changeCount = (value: number) => {
        if (value > 0)
            setCount(value);
    }

    const addToPayment = async () => {
        showLoader();

        const _ingredients = getIngredientsForOrderLine(ingredients, item.ingredientGroups);

        const r = await orderStore.addToOrder({
            id: guid(),
            menuId,
            subMenuId,
            recommendationGroupId,
            name: item.name,
            number: item.number,
            modifiers: modifiers,
            addons: addons,
            ingredients: _ingredients,
            count,
            price: additionalSellUnit?.price || item.price,
            total: price,
            img: item.previewList
        });

        if (r) {
            reset();
            // menuStore.addItemToOrder();

            if (menuStore.returnToScanArticle) {
                navigate("/scanArticle");
                hideLoader();

                return;
            }

            if (skipRecommendations)
                navigate("/menu");
            else
                navigate("/recommendations");
        }
        hideLoader();
    }

    const changeModifiers = (id: number, selectedModifiers: number[]) => {
        const _modifiers = lodash.cloneDeep(modifiers).filter((x: OrderLineModifiers) => x.groupId !== id);
        _modifiers.push({
            groupId: id,
            items: selectedModifiers
        });
        setModifiers(_modifiers);
    }

    const changeAddons = (id: number, selectedAddons: OrderLineAddon[]) => {
        const _addons = lodash.cloneDeep(addons).filter(x => x.groupId !== id);
        const addonGroup = item.addonGroups?.find(x => x.id === id);
        if (!addonGroup) return;

        _addons.push({
            groupId: id,
            groupFreeAddons: addonGroup.freeAddons || 0,
            items: selectedAddons
        });
        setAddons(_addons);
    }

    const changeIngredients = (id: string, selected: OrderLineIngredient[]) => {
        const group = item.ingredientGroups?.find(x => x.id === id);
        if (group) {
            const _ingridients = lodash.cloneDeep(ingredients).filter(x => x.groupId !== id);

            let allowAdd = true;
            if (group.max && selected.length > group.max) {
                allowAdd = false;
            }

            if (allowAdd) {
                _ingridients.push({
                    groupId: id,
                    items: selected
                });
                setIngredients(_ingridients);
            }
        }
    }

    const changeSellUnit = (sellUnit: AdditionalSellUnit) => {
        setAdditionalSellUnit(sellUnit);
    }

    const showModifierNotice = () => {
        showInfo(t("chooseModifier"), 3000);
    }

    return <div className="MenuItem">
        <div className="img">
            <img alt={item.name} src={item.previewEdit ? `${getHost()}${item.previewEdit}` : (styles?.article.defaultEditImage ? `${getHost()}${styles.article.defaultEditImage}` : defaultImg)} />
        </div>
        <div className="text">
            <div className="title">
                {item.name}
            </div>
            <div className="description">
                {item.description || ""}
            </div>
            <div className="allergens">
                {item.allergens?.map(allergenId => getAllergenById(allergenId)?.name || null).filter(name => !!name).map(name => <div key={name} className="allergen">{name}</div>)}
            </div>
        </div>

        {item.additionalSellUnits?.length
            ? <SellUnitsGroup sellUnits={uniqArray(item.additionalSellUnits, "price")} onChange={changeSellUnit} selected={additionalSellUnit || item.additionalSellUnits.find(x => x.isDefault) || item.additionalSellUnits[0]} />
            : null
        }

        {item.modifierGroups?.length
            ? item.modifierGroups.map(mg => <ModifiersGroup key={`modifiersGroup_${mg.name}`} modifiersGroup={mg} onChange={changeModifiers} selected={modifiers.find(x => x.groupId === mg.id)?.items || []} />)
            : null
        }

        {item.addonGroups?.length
            ? item.addonGroups.map(ag => <AddonsGroup key={`addonsGroup_${ag.id}`} addonsGroup={ag} onChange={changeAddons} selected={addons.find(x => x.groupId === ag.id)?.items || []} free={free}/>)
            : null
        }
        {item.ingredientGroups?.length
            ? item.ingredientGroups.map(ig => <IngredientsGroup key={`ingredientsGroup_${ig.id}`} ingredientsGroup={ig} onChange={changeIngredients} selected={ingredients.find(x => x.groupId === ig.id)?.items || []} />)
            : null
        }

        <div className="addToOrder">
            <Counter value={count} onChange={changeCount} />
            <div className="price">
                {(price * count).toFixed(2)} {getCurrency()}
            </div>
            <div className={`button dark ${!allowAddToOrder ? "disable" : ""}`} onClick={allowAddToOrder ? addToPayment : showModifierNotice}>
                {t("addToOrder")}
            </div>
        </div>
    </div>
}

export default MenuItemBlock;
